import React, { useEffect, useState } from 'react';
import TableContainer from '../TableContainer';
import DefaultModal from './DefaultModal';
import ConfirmModal from './ConfirmModal';
import useDefaultModal from '../hooks/useDefaultModal';
import useConfirmModal from '../hooks/useConfirmModal';
import SubelementoDespesaModal from './SubelementoDespesaModal';
import PaginationBar from '../PaginationBar';
import usePagination10 from '../hooks/usePagination10';
import DataTable from '../DataTable';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';

function SubelementoDespesasListModal({ show, onClose, elemento }) {
  const [list, setList] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [filtro, setFiltro] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'codigo', direction: 'asc' });

  const normalizeString = str => {
    if (!str) return '';
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
  };

  const sortedList = [...list].sort((a, b) => {
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    return String(a[sortConfig.key]).localeCompare(String(b[sortConfig.key]), 'pt-BR', { numeric: true }) * dir;
  });

  const filteredList = sortedList.filter(row => {
    const termo = normalizeString(filtro);
    return (
      normalizeString(row.codigo).includes(termo) ||
      normalizeString(row.descricao).includes(termo)
    );
  });

  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const token = localStorage.getItem('token');
  const { setPagination } = usePagination10(filteredList.length);

  const paginatedList = filteredList.slice(
    setPagination.startIndex,
    setPagination.startIndex + setPagination.rowsPerPage
  );

  const load = () => {
    if (!elemento) return;
    setLoading(true);
    axios.get(API_BASE_URL.SUBELEMENTO_DESPESA, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : [];
        setList(data.filter(item => String(item.idElemento) === String(elemento.codigo)));
      })
      .catch(err => showDefaultModal('error', err?.response?.data || err.message || 'Erro ao carregar'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (show) load();
  }, [show]);

  useEffect(() => {
    setPagination.onFirstItems();
  }, [filtro]);

  const handleNew = () => { setSelected(null); setShowModal(true); };
  const handleEdit = () => {
    if (!selectedRow) return;
    setSelected(selectedRow);
    setShowModal(true);
  };
  const handleDelete = () => {
    if (!selectedRow) return;
    showConfirmModal('delete', async () => {
      try {
        await axios.delete(`${API_BASE_URL.SUBELEMENTO_DESPESA}/${selectedRow.idElemento}/${selectedRow.codigo}`, { headers: { Authorization: `Bearer ${token}` } });
        showDefaultModal('success', 'Registro excluído');
        setSelectedRow(null);
        load();
      } catch (err) {
        showDefaultModal('error', err?.response?.data?.error || err?.response?.data || err.message || 'Erro ao excluir');
      }
    });
  };

  const handleSaved = () => { setShowModal(false); load(); };

  const handleRowClick = (row) => {
    setSelectedRow(prev => prev?.codigo === row.codigo && prev?.idElemento === row.idElemento ? null : row);
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const sortIcon = (key) => {
    if (sortConfig.key !== key) return <i className="bi bi-arrow-down-up ms-1" style={{ opacity: 0.3 }} />;
    return sortConfig.direction === 'asc'
      ? <i className="bi bi-sort-up ms-1" />
      : <i className="bi bi-sort-down ms-1" />;
  };

  if (!show) return null;

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable" style={{ maxWidth: '95vw', height: '90vh' }} onClick={e => e.stopPropagation()}>
        <div className="modal-content h-100">
          <div className="modal-header text-white" style={{ background: 'linear-gradient(45deg, rgb(7, 29, 65) 0%, rgba(29, 100, 216, 1) 100%)', padding: '0.5rem 1rem' }}>
            <h6 className="modal-title mb-0">Subelementos de Despesa</h6>
            <button type="button" className="btn-close btn-close-white" onClick={onClose} aria-label="Fechar" />
          </div>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column' }}>
            <div className="mb-2 p-2 bg-light rounded border">
              <strong>Elemento de Despesa:</strong> {elemento?.codigo} - {elemento?.descricao}
            </div>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="d-flex gap-1">
                <button className="btn btn-success btn-sm" onClick={handleNew}>
                  <i className="bi bi-plus-circle me-1" /> Novo
                </button>
                <button className="btn btn-primary btn-sm" onClick={handleEdit} disabled={!selectedRow}>
                  <i className="bi bi-pencil me-1" /> Editar
                </button>
                <button className="btn btn-danger btn-sm" onClick={handleDelete} disabled={!selectedRow}>
                  <i className="bi bi-trash me-1" /> Excluir
                </button>
              </div>
              <div className="input-group input-group-sm" style={{ maxWidth: '280px' }}>
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Buscar por código ou descrição"
                  value={filtro}
                  onChange={e => setFiltro(e.target.value.toUpperCase())}
                />
              </div>
            </div>

            <TableContainer isLoading={isLoading}>
              <DataTable style={{ fontSize: '0.8rem' }} head={
                <tr>
                  <th style={{ width: '80px', cursor: 'pointer' }} onClick={() => handleSort('codigo')}>
                    Código {sortIcon('codigo')}
                  </th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('descricao')}>
                    Descrição {sortIcon('descricao')}
                  </th>
                </tr>}>
                <tbody>
                  {paginatedList && paginatedList.length > 0 ? paginatedList.map((row) => (
                    <tr
                      key={`${row.idElemento}_${row.codigo}`}
                      onClick={() => handleRowClick(row)}
                      className={selectedRow?.codigo === row.codigo && selectedRow?.idElemento === row.idElemento ? 'table-primary' : ''}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{row.codigo}</td>
                      <td>{row.descricao}</td>
                    </tr>
                  )) : (
                    <tr><td colSpan={2} className="text-center">Nenhum registro encontrado.</td></tr>
                  )}
                </tbody>
              </DataTable>
            </TableContainer>
          </div>
          <PaginationBar setPagination={setPagination} />
        </div>
      </div>

      <SubelementoDespesaModal show={showModal} onClose={() => setShowModal(false)} onSave={handleSaved} data={selected} />
      <DefaultModal {...setDefaultModal} />
      <ConfirmModal {...setConfirmModal} />
    </div>
  );
}

export default SubelementoDespesasListModal;
